import React, { useState, useEffect, useRef, useCallback, Fragment, memo } from "react";
import { Pause, Play } from "lucide-react";
import WaveSurfer from "wavesurfer.js";

const CustomAudioPlayer = memo(({ src, wid, CustomStyle }) => {
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingPercentage, setLoadingPercentage] = useState(0);
  const [progressTime, setProgressTime] = useState("0:00");

  const formatTime = useCallback((timeInSeconds) => {
    const minutes = Math.floor((timeInSeconds % 3600) / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${("00" + seconds).slice(-2)}`;
  }, []);

  const cleanupWaveSurfer = useCallback(() => {
    if (wavesurferRef.current) {
      wavesurferRef.current.pause();
      wavesurferRef.current.unAll(); // Remove all event listeners
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }
  }, []);

  const initWaveSurfer = useCallback(() => {
    try {
      wavesurferRef.current = WaveSurfer.create({
        container: waveformRef.current,
        waveColor: "#A3A3A3",
        progressColor: "#A3A3A3",
        cursorColor: "#ff2800",
        barWidth: 2,
        barGap: 1,
        cursorWidth: 1,
        mediaControls: false,
        autoplay: false,
        width: wid || 350,
        barHeight: 60,
        fillParent: true,
      });

      setLoading(true);
      wavesurferRef.current.load(src);

      wavesurferRef.current.on("ready", () => {
        const audioDuration = wavesurferRef.current.getDuration();
        setDuration(audioDuration);
        setLoading(false);
      });

      wavesurferRef.current.on("loading", (percentage) => {
        setLoadingPercentage(percentage);
      });

      wavesurferRef.current.on("timeupdate", (currentTime) => {
        setProgressTime(formatTime(currentTime));
      });

      wavesurferRef.current.on("error", (err) => {
        console.error("WaveSurfer error:", err);
        setLoading(false);
      });
    } catch (error) {
      console.error("Error initializing WaveSurfer:", error);
      setLoading(false);
    }
  }, [src, wid, formatTime]);

  // Mount effect
  useEffect(() => {
    initWaveSurfer();
    return () => {
      cleanupWaveSurfer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle src changes
  const prevSrcRef = useRef(src);
  useEffect(() => {
    if (prevSrcRef.current !== src) {
      prevSrcRef.current = src;
      cleanupWaveSurfer();
      initWaveSurfer();
    }
  }, [src, cleanupWaveSurfer, initWaveSurfer]);

  const togglePlay = useCallback(() => {
    if (!wavesurferRef.current) return;

    if (isPlaying) {
      wavesurferRef.current.pause();
    } else {
      wavesurferRef.current.play();
    }

    setIsPlaying((prev) => !prev);
  }, [isPlaying]);

  const defaultStyle = {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    flex: 1,
    minWidth: "100%",
  };

  return (
    <div style={CustomStyle || defaultStyle}>
      {loading ? (
        <p>{loadingPercentage + "%"}</p>
      ) : (
        <Fragment>
          <div style={{ display: "flex" }} className="hover">
            {isPlaying ? (
              <Pause size={16} onClick={togglePlay} />
            ) : (
              <Play size={16} onClick={togglePlay} />
            )}
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{duration ? formatTime(duration) : ""}</span>
            <span>{progressTime}</span>
          </div>
        </Fragment>
      )}
      <div
        className="CustomAudioPlayerWaveRef"
        ref={waveformRef}
        style={{ flex: 1, minWidth: "100%" }}
      ></div>
    </div>
  );
});

export default CustomAudioPlayer;
